import { Suspense, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import ToolCard from '../../components/feature/ToolCard';
import { allTools } from '../../mocks/tools';
import { useSEO } from '../../hooks/useSEO';
import { categoryEditorial } from '../../mocks/category-editorial';

// Related categories for each category slug
const RELATED_CATEGORIES_MAP: Record<string, string[]> = {
  'ai-writing-tools':      ['AI Image Generators',   'AI Marketing Tools',     'AI Productivity Tools',  'AI Video Tools'],
  'ai-image-generators':   ['AI Video Tools',         'AI Design Tools',        'AI Writing Tools',       'AI Marketing Tools'],
  'ai-video-tools':        ['AI Image Generators',    'AI Voice Tools',         'AI Writing Tools',       'AI Marketing Tools'],
  'ai-website-builders':   ['AI Design Tools',        'AI Coding Tools',        'AI Marketing Tools',     'AI Writing Tools'],
  'ai-marketing-tools':    ['AI Writing Tools',       'AI Automation Tools',    'AI Productivity Tools',  'AI Design Tools'],
  'ai-productivity-tools': ['AI Automation Tools',    'AI Writing Tools',       'AI Coding Tools',        'AI Marketing Tools'],
  'ai-coding-tools':       ['AI Productivity Tools',  'AI Automation Tools',    'AI Website Builders',    'AI Writing Tools'],
  'ai-design-tools':       ['AI Image Generators',    'AI Video Tools',         'AI Marketing Tools',     'AI Website Builders'],
  'ai-voice-tools':        ['AI Video Tools',         'AI Productivity Tools',  'AI Marketing Tools',     'AI Writing Tools'],
  'ai-automation-tools':   ['AI Productivity Tools',  'AI Coding Tools',        'AI Marketing Tools',     'AI Website Builders'],
};

function categoryNameToSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

// Slug to category name mapping
const slugToCategoryMap: Record<string, string> = {
  'ai-writing-tools': 'AI Writing Tools',
  'ai-image-generators': 'AI Image Generators',
  'ai-video-tools': 'AI Video Tools',
  'ai-website-builders': 'AI Website Builders',
  'ai-marketing-tools': 'AI Marketing Tools',
  'ai-productivity-tools': 'AI Productivity Tools',
  'ai-coding-tools': 'AI Coding Tools',
  'ai-design-tools': 'AI Design Tools',
  'ai-voice-tools': 'AI Voice Tools',
  'ai-automation-tools': 'AI Automation Tools',
};

// Category descriptions
const categoryDescriptions: Record<string, string> = {
  'AI Writing Tools': 'Discover powerful AI writing assistants that help you create content faster, improve your writing quality, and overcome writer\'s block. From blog posts to marketing copy, these tools make writing effortless.',
  'AI Image Generators': 'Explore cutting-edge AI image generation tools that turn your text prompts into stunning visuals. Perfect for designers, marketers, and creators who need unique images without photography or design skills.',
  'AI Video Tools': 'Transform your video creation workflow with AI-powered tools that handle editing, generation, and enhancement. Create professional videos in minutes without expensive equipment or technical expertise.',
  'AI Website Builders': 'Build beautiful, functional websites using AI-powered builders that understand plain language. No coding required — just describe what you want and watch your site come to life.',
  'AI Marketing Tools': 'Supercharge your marketing campaigns with AI tools that optimize ads, generate copy, analyze data, and automate workflows. Get better results with less manual work.',
  'AI Productivity Tools': 'Boost your efficiency with AI productivity tools that automate tasks, manage your schedule, take notes, and help you focus on what matters most.',
  'AI Coding Tools': 'Code faster and smarter with AI coding assistants that autocomplete code, catch bugs, explain complex logic, and help you learn new programming languages.',
  'AI Design Tools': 'Create stunning designs with AI-powered tools that generate graphics, enhance images, and bring your creative vision to life — no design degree required.',
  'AI Voice Tools': 'Transform audio with AI voice tools that generate realistic speech, clone voices, transcribe audio, and remove background noise. Perfect for content creators and podcasters.',
  'AI Automation Tools': 'Automate repetitive tasks and build powerful AI workflows without code. Connect your apps, deploy autonomous agents, and let AI handle complex multi-step processes so you can focus on what matters.',
};

// SEO metadata for each category
const categorySEO: Record<string, { title: string; description: string }> = {
  'ai-writing-tools': {
    title: 'Best AI Writing Tools 2026 - Top Picks & Reviews | ToolNova',
    description: 'Browse the top AI writing tools including Jasper, Copy.ai, Grammarly, and more. Compare features, pricing, and find the best writing AI for your needs.',
  },
  'ai-image-generators': {
    title: 'Best AI Image Generators 2026 - Compare Top Tools | ToolNova',
    description: 'Discover the best AI image generation tools including Midjourney, DALL·E 3, Adobe Firefly, and Leonardo.ai. Find the right image AI for your creative workflow.',
  },
  'ai-video-tools': {
    title: 'Best AI Video Tools 2026 - Top Video AI Software | ToolNova',
    description: 'Explore the top AI video tools including Runway, Sora, HeyGen, Synthesia, and more. Create, edit, and generate videos with AI.',
  },
  'ai-website-builders': {
    title: 'Best AI Website Builders 2026 - No-Code AI Sites | ToolNova',
    description: 'Compare the best AI website builders including Readdy, Framer AI, Durable, Wix AI, and 10Web. Build your site faster with AI.',
  },
  'ai-marketing-tools': {
    title: 'Best AI Marketing Tools 2026 - Top Picks for Growth | ToolNova',
    description: 'Discover the top AI marketing tools including HubSpot AI, Surfer SEO, Mailchimp AI, and Adcreative.ai. Automate and scale your marketing with AI.',
  },
  'ai-productivity-tools': {
    title: 'Best AI Productivity Tools 2026 - Work Smarter | ToolNova',
    description: 'Browse the top AI productivity tools including Notion AI, Otter.ai, Reclaim AI, and Fireflies.ai. Get more done with AI assistants.',
  },
  'ai-coding-tools': {
    title: 'Best AI Coding Tools 2026 - Top AI for Developers | ToolNova',
    description: 'Explore the best AI coding tools including GitHub Copilot, Cursor, Devin AI, and Bolt.new. Write better code faster with AI.',
  },
  'ai-design-tools': {
    title: 'Best AI Design Tools 2026 - Top Creative AI | ToolNova',
    description: 'Discover the top AI design tools including Canva AI, Looka, Adobe Firefly, and more. Create stunning designs with artificial intelligence.',
  },
  'ai-voice-tools': {
    title: 'Best AI Voice Tools 2026 - Text to Speech & Voice AI | ToolNova',
    description: 'Discover the best AI voice tools including ElevenLabs, Murf AI, and Krisp. Generate realistic voiceovers, clone voices, and remove background noise with AI.',
  },
  'ai-automation-tools': {
    title: 'Best AI Automation Tools 2026 - No-Code Workflow AI | ToolNova',
    description: 'Discover the best AI automation tools including Zapier, Make, n8n, and AgentGPT. Build autonomous AI agents and automate workflows without code.',
  },
};

function generateCategoryOgImage(slug: string): string {
  const prompt = `ToolNova AI tools category page for ${slug.replace(/-/g, ' ')}, dark navy background, glowing teal accent color, futuristic SaaS design, minimal abstract neural pattern, clean professional open graph social preview card, 1200x630`;
  return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28prompt%29%7D&width=1200&height=630&seq=tn-cat-${slug}&orientation=landscape`;
}

export default function CategoryPage() {
  const { categorySlug: slug } = useParams<{ categorySlug: string }>();
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  const categoryName = slug ? slugToCategoryMap[slug] : undefined;
  const seoData = slug ? categorySEO[slug] : undefined;
  const editorial = slug ? categoryEditorial[slug] : undefined;

  const filteredTools = useMemo(() => {
    if (!categoryName) return [];
    return allTools.filter(tool => tool.category === categoryName);
  }, [categoryName]);

  const handleBackToCategories = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('categories');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const relatedCategories = slug ? (RELATED_CATEGORIES_MAP[slug] ?? []) : [];

  // Build FAQPage schema entries if we have FAQ data
  const faqSchema = editorial?.faqs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: editorial.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      }
    : undefined;

  const collectionSchema = categoryName
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: categoryName,
        description: categoryDescriptions[categoryName],
        url: `${siteUrl}/category/${slug}`,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: filteredTools.length,
          itemListElement: filteredTools.slice(0, 10).map((tool, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'SoftwareApplication',
              name: tool.name,
              description: tool.description,
              url: tool.website,
            },
          })),
        },
      }
    : undefined;

  const itemListSchema = categoryName
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: categoryName,
        itemListElement: filteredTools.map((tool, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${siteUrl}/tool/${tool.slug}`,
          name: tool.name,
        })),
      }
    : undefined;

  const schema = [collectionSchema, faqSchema, itemListSchema].filter(Boolean) as object[];

  useSEO({
    title: seoData?.title || (categoryName ? `${categoryName} - Best AI Tools for ${categoryName.replace('AI ', '')} | Toolnova` : 'Category Not Found'),
    description: seoData?.description || (categoryName ? `Discover the best ${categoryName.toLowerCase()} for creators and businesses. Compare features, pricing, and reviews of ${filteredTools.length} top AI tools.` : 'Category not found'),
    keywords: categoryName ? `${categoryName}, AI tools, ${categoryName.toLowerCase()} reviews, best ${categoryName.toLowerCase()}` : '',
    canonical: categoryName ? `${siteUrl}/category/${slug}` : undefined,
    ogTitle: seoData?.title || (categoryName ? `${categoryName} - Toolnova` : undefined),
    ogDescription: seoData?.description || (categoryName ? categoryDescriptions[categoryName] : undefined),
    ogType: 'website',
    ogUrl: categoryName ? `${siteUrl}/category/${slug}` : undefined,
    ogImage: slug ? generateCategoryOgImage(slug) : undefined,
    noindex: !categoryName,
    schema: schema.length > 0 ? schema : undefined,
  });

  if (!categoryName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
        <Navbar />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Category Not Found</h1>
            <p className="text-gray-400 mb-8">The category you&apos;re looking for doesn&apos;t exist.</p>
            <button
              onClick={handleBackToCategories}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Back to All Categories
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      <Navbar />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">

          {/* Breadcrumb */}
          <nav className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-400 hover:text-teal-300 transition-colors duration-200 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <button onClick={handleBackToCategories} className="text-gray-400 hover:text-teal-300 transition-colors duration-200 cursor-pointer">Categories</button>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <span className="text-white font-medium">{categoryName}</span>
          </nav>

          {/* Back Button */}
          <div className="mb-8">
            <button
              onClick={handleBackToCategories}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 hover:border-teal-500/60 text-gray-400 hover:text-teal-300 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to All Categories
            </button>
          </div>

          {/* Category Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{categoryName}</h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
              {categoryDescriptions[categoryName]}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="px-4 py-2 bg-teal-500/15 border border-teal-500/30 rounded-lg text-teal-300 text-sm font-semibold">
                {filteredTools.length} {filteredTools.length === 1 ? 'Tool' : 'Tools'}
              </span>
            </div>
          </div>

          {/* Intro paragraph */}
          {editorial?.intro && (
            <div className="mb-10 bg-gray-900/40 border border-gray-800/80 rounded-xl p-7">
              <p className="text-gray-300 text-base leading-relaxed">{editorial.intro}</p>
            </div>
          )}

          {/* Tools Grid */}
          {filteredTools.length > 0 ? (
            <Suspense fallback={<div className="text-center text-gray-400">Loading tools...</div>}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                {filteredTools.map((tool) => (
                  <ToolCard key={tool.id} tool={tool} />
                ))}
              </div>
            </Suspense>
          ) : (
            <div className="text-center py-20 mb-16">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800/50 rounded-full mx-auto mb-6">
                <i className="ri-tools-line text-4xl text-gray-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">No Tools Found</h3>
              <p className="text-gray-400 mb-8">We&apos;re still adding tools to this category. Check back soon!</p>
              <button
                onClick={handleBackToCategories}
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Browse All Categories
              </button>
            </div>
          )}

          {/* ── EDITORIAL CONTENT ─────────────────────────────────────────── */}
          {editorial && (
            <div className="space-y-14 border-t border-gray-800/60 pt-14">

              {/* H2 Sections */}
              {editorial.h2Sections?.map((section, i) => (
                <section key={i}>
                  <h2 className="text-2xl font-bold text-white mb-4">{section.heading}</h2>
                  <div className="bg-gray-900/40 border border-gray-800/80 rounded-xl p-7">
                    <p className="text-gray-300 text-base leading-relaxed">{section.body}</p>
                  </div>
                </section>
              ))}

              {/* What to Look For */}
              {editorial.whatToLookFor?.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">What to Look for in {categoryName}</h2>
                  <div className="bg-gray-900/40 border border-gray-800/80 rounded-xl p-7">
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {editorial.whatToLookFor.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-teal-400 text-lg mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* Best For */}
              {editorial.bestFor?.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-4">Who Are These Tools Best For?</h2>
                  <div className="bg-gray-900/40 border border-gray-800/80 rounded-xl p-7">
                    <ul className="space-y-3">
                      {editorial.bestFor.map((item, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <i className="ri-user-star-fill text-teal-400 text-base flex-shrink-0"></i>
                          <span className="text-gray-300 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* FAQ Section */}
              {editorial.faqs?.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {editorial.faqs.map((faq, i) => (
                      <FAQItem key={i} question={faq.question} answer={faq.answer} defaultOpen={i === 0} />
                    ))}
                  </div>
                </section>
              )}

              {/* Related Categories */}
              {relatedCategories.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-white mb-5">Explore Related Categories</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedCategories.map((cat) => (
                      <Link
                        key={cat}
                        to={`/category/${categoryNameToSlug(cat)}`}
                        className="flex items-center justify-between gap-3 px-6 py-4 bg-gray-900/40 border border-gray-800/80 hover:border-teal-500/40 hover:bg-gray-900/60 rounded-xl transition-all duration-200 group cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <i className="ri-apps-2-line text-teal-500/70 text-lg"></i>
                          </div>
                          <span className="text-white font-semibold text-sm group-hover:text-teal-300 transition-colors truncate">
                            {cat}
                          </span>
                        </div>
                        <i className="ri-arrow-right-line text-gray-600 group-hover:text-teal-400 transition-colors flex-shrink-0"></i>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

function FAQItem({ question, answer, defaultOpen = false }: { question: string; answer: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-gray-900/40 border border-gray-800/80 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left cursor-pointer group"
      >
        <h4 className="text-base font-semibold text-white group-hover:text-teal-300 transition-colors">
          {question}
        </h4>
        <i className={`ri-arrow-down-s-line text-xl text-gray-500 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
      </button>
      <div
        className={`grid transition-all duration-200 ease-in-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          <div className="px-7 pb-6 border-t border-gray-800/60 pt-4">
            <p className="text-gray-300 text-sm leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
